// UserActionStats.jsx - Admin view for aggregated user action stats and per-user drill-down
import React, { useState, useEffect, useCallback } from "react";
import { API_BASE } from "../../config/api";
import { httpRequest } from "../../utils/httpClient";
import { authService } from "../../services/auth";
import logger from '../../utils/logger';

function StatCard({ label, value, sub }) {
  return (
    <div
      style={{
        padding: "1.25rem",
        borderRadius: "8px",
        border: "1px solid var(--border-color, #ddd)",
        background: "var(--bg-secondary, #fafafa)",
        display: "flex",
        flexDirection: "column",
        gap: "0.25rem",
      }}
    >
      <div
        style={{
          fontSize: "0.8rem",
          color: "var(--text-secondary, #666)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: "1.75rem",
          fontWeight: "700",
          color: "var(--text-primary, #333)",
          lineHeight: 1.1,
        }}
      >
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: "0.78rem", color: "var(--text-secondary, #888)" }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function formatNumber(n) {
  return n === null || n === undefined ? "—" : Number(n).toLocaleString();
}

function formatDate(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString();
}

function formatMetadata(metadata) {
  if (!metadata || Object.keys(metadata).length === 0) return "—";
  const text = JSON.stringify(metadata, null, 2);
  return text.length > 200 ? `${text.slice(0, 200)}…` : text;
}

function UserActionStats({ _user }) {
  const [summary, setSummary] = useState(null);
  const [workflows, setWorkflows] = useState(null);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userEvents, setUserEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState("7d");

  const fetchAggregate = useCallback(async () => {
    const days = parseInt(dateRange, 10);
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, workflowsRes, usersRes] = await Promise.all([
        httpRequest(`${API_BASE}/analytics/summary?days=${days}`, {
          method: "GET",
          headers: authService.getAuthHeaders(),
        }),
        httpRequest(`${API_BASE}/analytics/workflows?days=${days}`, {
          method: "GET",
          headers: authService.getAuthHeaders(),
        }),
        httpRequest(`${API_BASE}/admin/users?per_page=1000`, {
          method: "GET",
          headers: authService.getAuthHeaders(),
        }),
      ]);

      if (!summaryRes.ok) throw new Error(`Summary HTTP ${summaryRes.status}`);
      if (!workflowsRes.ok) throw new Error(`Workflows HTTP ${workflowsRes.status}`);
      if (!usersRes.ok) throw new Error(`Users HTTP ${usersRes.status}`);

      setSummary(await summaryRes.json());
      setWorkflows(await workflowsRes.json());
      const usersData = await usersRes.json();
      setUsers(usersData.users || []);
    } catch (err) {
      logger.error("User action stats fetch failed:", err);
      setError("Failed to load user action stats");
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchAggregate();
  }, [fetchAggregate]);

  const fetchUserEvents = useCallback(async (userId) => {
    setUserEvents([]);
    try {
      const res = await httpRequest(
        `${API_BASE}/analytics/events?user_id=${encodeURIComponent(userId)}&limit=10`,
        {
          method: "GET",
          headers: authService.getAuthHeaders(),
        }
      );
      if (!res.ok) throw new Error(`Events HTTP ${res.status}`);
      const data = await res.json();
      setUserEvents(data.events || []);
    } catch (err) {
      logger.error("User events fetch failed:", err);
    }
  }, []);

  const handleUserSelect = useCallback((user) => {
    setSelectedUser(user);
    fetchUserEvents(user.id);
  }, [fetchUserEvents]);

  const filteredUsers = users.filter((u) =>
    (u.email || "").toLowerCase().includes(userSearch.toLowerCase()) ||
    (u.username || "").toLowerCase().includes(userSearch.toLowerCase()) ||
    String(u.id).includes(userSearch)
  );

  const sortedActions = summary
    ? Object.entries(summary.action_breakdown || {}).sort((a, b) => b[1] - a[1])
    : [];

  return (
    <div className="admin-section" data-testid="user-action-stats-page">
      <h2>User Actions</h2>

      {error && (
        <p style={{ color: "var(--error-color, #d32f2f)" }}>{error}</p>
      )}
      {loading && (
        <p style={{ color: "var(--text-secondary, #666)" }}>Loading…</p>
      )}

      <div className="admin-controls" style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "center" }}>
        <label style={{ color: "var(--text-primary, #333)" }}>
          Date Range:{" "}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="admin-search-input"
            style={{ width: "auto", marginLeft: "0.5rem" }}
          >
            <option value="1d">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </label>
      </div>

      {summary && (
        <>
          <h3 style={{ margin: "1.5rem 0 0.75rem", color: "var(--text-primary, #333)" }}>
            Aggregate Stats
          </h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
              gap: "1rem",
              marginBottom: "1.5rem",
            }}
          >
            <StatCard label="Total Events" value={formatNumber(summary.total_events)} />
            <StatCard label="Unique Users" value={formatNumber(summary.unique_users)} />
            <StatCard label="Unique Sessions" value={formatNumber(summary.unique_sessions)} />
            <StatCard label="Avg Events / User" value={formatNumber(summary.avg_events_per_user)} />
          </div>

          <h3 style={{ margin: "1.5rem 0 0.75rem", color: "var(--text-primary, #333)" }}>
            Most Used Actions
          </h3>
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {sortedActions.map(([action, count]) => (
                  <tr key={action}>
                    <td>{action}</td>
                    <td>{formatNumber(count)}</td>
                  </tr>
                ))}
                {sortedActions.length === 0 && (
                  <tr>
                    <td colSpan={2}>No actions in this range.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {workflows && (
        <>
          <h3 style={{ margin: "1.5rem 0 0.75rem", color: "var(--text-primary, #333)" }}>
            Top Workflows
          </h3>
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>From</th>
                  <th>To</th>
                  <th>Count</th>
                </tr>
              </thead>
              <tbody>
                {workflows.top_pairs.map((pair, idx) => (
                  <tr key={`${pair.from}-${pair.to}-${idx}`}>
                    <td>{pair.from}</td>
                    <td>{pair.to}</td>
                    <td>{formatNumber(pair.count)}</td>
                  </tr>
                ))}
                {workflows.top_pairs.length === 0 && (
                  <tr>
                    <td colSpan={3}>No workflow data in this range.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <h3 style={{ margin: "1.5rem 0 0.75rem", color: "var(--text-primary, #333)" }}>
        Select a User
      </h3>
      <div className="admin-controls">
        <input
          type="text"
          placeholder="Search by email, username, or ID…"
          value={userSearch}
          onChange={(e) => setUserSearch(e.target.value)}
          className="admin-search-input"
        />
      </div>
      <div className="admin-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Email</th>
              <th>Username</th>
              <th>BGG ID</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.slice(0, 20).map((u) => (
              <tr key={u.id}>
                <td>{u.id}</td>
                <td>{u.email || "N/A"}</td>
                <td>{u.username || "N/A"}</td>
                <td>{u.bgg_id?.trim() || "N/A"}</td>
                <td>
                  <button
                    className="admin-btn admin-btn-primary"
                    onClick={() => handleUserSelect(u)}
                    data-testid={`view-user-${u.id}`}
                  >
                    View Last 10
                  </button>
                </td>
              </tr>
            ))}
            {filteredUsers.length === 0 && (
              <tr>
                <td colSpan={5}>No users found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedUser && (
        <>
          <h3 style={{ margin: "1.5rem 0 0.75rem", color: "var(--text-primary, #333)" }}>
            Last 10 actions for {selectedUser.username || selectedUser.email || `User ${selectedUser.id}`}
          </h3>
          <div className="admin-table-container" data-testid="user-events-table">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Action</th>
                  <th>Page</th>
                  <th>Metadata</th>
                </tr>
              </thead>
              <tbody>
                {userEvents.map((e) => (
                  <tr key={e.id}>
                    <td>{formatDate(e.timestamp)}</td>
                    <td>{e.action_type}</td>
                    <td>{e.page_url || "—"}</td>
                    <td>
                      <pre
                        style={{
                          margin: 0,
                          whiteSpace: "pre-wrap",
                          fontSize: "0.85rem",
                          color: "var(--text-secondary, #666)",
                        }}
                      >
                        {formatMetadata(e.metadata)}
                      </pre>
                    </td>
                  </tr>
                ))}
                {userEvents.length === 0 && (
                  <tr>
                    <td colSpan={4}>No actions found for this user.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

export default UserActionStats;
